import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Logo } from '@/shared/ui/logo';

type AuthLayoutProps = {
  /** The page's `h1`, in Sectra. */
  title: ReactNode;
  /** The line under the form ("No account? Create one"). */
  footer?: ReactNode;
  /** Render as a plain block instead of the page's `main` (the design page preview). */
  embedded?: boolean;
  children: ReactNode;
};

/** Signed-out pages: a centred white card on the pale blue ground with the logo above it. */
function AuthLayout({ title, footer, embedded = false, children }: AuthLayoutProps) {
  const Frame = embedded ? 'div' : 'main';
  return (
    <Frame
      className={cn(
        'flex flex-col items-center justify-center gap-8 px-4 py-12',
        !embedded && 'min-h-dvh',
      )}
    >
      <Logo className="w-36" />
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle asChild className="text-3xl">
            <h1>{title}</h1>
          </CardTitle>
        </CardHeader>
        <CardContent>{children}</CardContent>
        {footer ? <CardFooter className="text-sm">{footer}</CardFooter> : null}
      </Card>
    </Frame>
  );
}

export { AuthLayout, type AuthLayoutProps };
